import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { PlusIcon } from '@rsos/assets/icons';
import { selectUserOrgByApp } from '@rsos/sinatra';
import { displayMilitaryTime } from '@rsos/utils/timeHelpers';
import type { RootState } from '../../../app';
import { useConversationSummary } from '../../../contexts/ConversationsContext';
import {
  useListInteragencyConversationsQuery,
  type InteragencyConversationItem,
} from '../../../reduxDataStore/interagency/interagencyAPI';
import {
  setSelectedInteragencyChat,
  useSelectedInteragencyChat,
} from '../../../reduxDataStore/interagency/interagencySlice';
import styles from './AgencyConversationsList.module.scss';
import NewAgencyConversationModal from './NewAgencyConversationModal';

function ConversationRow({
  conversation,
  isSelected,
  onSelect,
}: {
  conversation: InteragencyConversationItem;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const { summary } = useConversationSummary(conversation.conversation_id);
  const lastMessage = summary?.lastMessage ?? null;
  const isUnread = summary?.isUnread ?? false;

  const timestamp =
    lastMessage?.createdAt ?? conversation.extra_info.last_updated_time;

  return (
    <button
      className={`${styles.conversationRow} ${isSelected ? styles.conversationRowSelected : ''}`}
      onClick={onSelect}
      type="button"
    >
      <div className={styles.conversationRowHeader}>
        {isUnread && (
          <span
            className={styles.unreadDot}
            role="status"
            aria-label="Unread messages"
          />
        )}
        <span className={styles.conversationName}>
          {conversation.conversation_name}
        </span>
        <span className={styles.timestamp}>
          {displayMilitaryTime(timestamp)}
        </span>
      </div>
      {lastMessage?.senderName && (
        <span
          className={styles.senderName}
          data-name="agency-conversation-row-sender-name"
        >
          {lastMessage.senderName}
          {lastMessage.senderAgencyName
            ? ` (${lastMessage.senderAgencyName})`
            : ''}
        </span>
      )}
      <span
        className={styles.lastMessage}
        data-name="agency-conversation-row-last-message"
      >
        {lastMessage?.text ?? ''}
      </span>
    </button>
  );
}

/**
 * Rendered inside the Agency Chat tab in the call queue sidebar (Queue.js).
 * Shows the list of interagency conversations for the current authority
 * and provides a "+" button to create new ones via the agency directory.
 * Gated by the INTERAGENCY_CHAT capability at the Queue level.
 */
export default function AgencyConversationsList() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const selectedConversationId = useSelectedInteragencyChat();
  const dispatch = useDispatch();

  const orgName = useSelector(
    (state: RootState) =>
      selectUserOrgByApp(
        state.sinatra,
        state.sinatra.user.currentRole?.application ?? '',
      )?.name,
  );

  const { data, isLoading, isError } = useListInteragencyConversationsQuery(
    { orgName: orgName ?? '' },
    { skip: !orgName },
  );

  const conversations = data?.conversations ?? [];

  return (
    <>
      <div className={styles.header}>
        <span className={styles.headerTitle}>Agency Conversations</span>
        <button
          className={styles.plusButton}
          data-name="create-agency-conversation"
          onClick={() => setIsModalOpen(true)}
          type="button"
        >
          <PlusIcon width={14} height={14} />
        </button>
      </div>
      <div className={styles.scrollableList}>
        {conversations.length === 0 ? (
          <div className={styles.emptyState}>
            {isLoading
              ? 'Loading conversations...'
              : isError
                ? 'Failed to load conversations'
                : 'No conversations'}
          </div>
        ) : (
          conversations.map(conversation => (
            <ConversationRow
              key={conversation.conversation_id}
              conversation={conversation}
              isSelected={
                selectedConversationId === conversation.conversation_id
              }
              onSelect={() =>
                dispatch(
                  setSelectedInteragencyChat(conversation.conversation_id),
                )
              }
            />
          ))
        )}
      </div>
      <NewAgencyConversationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        orgName={orgName}
      />
    </>
  );
}
